import React, { useState, useEffect } from "react";
import { List, Avatar, Input, Button, Tooltip, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import "./UsersList.css";
import { isAuthenticated } from "../../Auth/auth";
import { BiMessageDetail } from "react-icons/bi";
import axios from "axios";
import { useTranslation } from "react-i18next";

const { Search } = Input;

export const extractPartner = (item, currentAuth) => {
  if (!item) return null;

  // Direct partner property if enriched by modern listConversations
  if (item.partner && item.partner._id) {
    return {
      _id: String(item.partner._id),
      firstName: item.partner.firstName || item.partner.name || "",
      lastName: item.partner.lastName || "",
      role: item.partner.role,
      picture: item.partner.picture,
      email: item.partner.email
    };
  }

  // Direct user object
  if (item._id && (item.firstName || item.lastName || item.email || item.username || item.name)) {
    return {
      _id: String(item._id),
      firstName: item.firstName || item.name || item.username || item.email || "",
      lastName: item.lastName || "",
      role: item.role,
      picture: item.picture
    };
  }

  const myIds = new Set(
    [
      currentAuth?._id,
      currentAuth?.id,
      currentAuth?.serviceProviderId,
      currentAuth?.employeeId
    ]
      .filter(Boolean)
      .map(id => String(id))
  );

  const parseEntity = val => {
    if (!val) return null;
    if (typeof val === "string") return { _id: val };
    if (val.id && typeof val.id === "object") {
      return {
        _id: String(val.id._id || val.id.id || ""),
        firstName: val.id.firstName || val.id.name || val.id.username || "",
        lastName: val.id.lastName || "",
        role: val.id.role,
        picture: val.id.picture
      };
    }
    if (val.id && typeof val.id === "string") {
      return {
        _id: val.id,
        role: val.model === "Employee" ? 1 : val.model === "ServiceProvider" ? 2 : 0
      };
    }
    if (val._id) {
      return {
        _id: String(val._id),
        firstName: val.firstName || val.name || "",
        lastName: val.lastName || "",
        role: val.role,
        picture: val.picture
      };
    }
    return null;
  };

  const senderObj = parseEntity(item.sender);
  const receiverObj = parseEntity(item.receiver);

  let partner = null;
  if (senderObj?._id && myIds.has(String(senderObj._id))) {
    partner = receiverObj;
  } else if (receiverObj?._id && myIds.has(String(receiverObj._id))) {
    partner = senderObj;
  } else {
    partner = receiverObj?._id ? receiverObj : senderObj;
  }

  if (partner && partner._id && partner._id !== "undefined" && partner._id !== "null") {
    return partner;
  }

  if (item._id && String(item._id) !== "undefined" && String(item._id) !== "null") {
    return {
      _id: String(item._id),
      firstName: item.name || item.firstName || "User",
      lastName: item.lastName || "",
      role: item.role,
      picture: item.picture
    };
  }

  return null;
};

const UserList = ({ users, onSelectUser, selectedUser }) => {
  const { t } = useTranslation("dashboard");
  const [searchText, setSearchText] = useState("");
  const [createNewChat, setCreateNewChat] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const authUser = isAuthenticated();

  const handleSearch = e => {
    setSearchText(e.target.value);
  };

  useEffect(() => {
    if (!createNewChat) return;
    let isCancelled = false;
    setLoadingContacts(true);
    const token = localStorage.getItem("token");
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/users/search`, {
        params: { query: searchText || "" },
        headers: { Authorization: "Bearer " + token }
      })
      .then(res => {
        if (!isCancelled && res.data?.results) {
          const myId = String(authUser?._id || authUser?.serviceProviderId || "");
          const valid = res.data.results.filter(
            u => u && u._id && String(u._id) !== myId
          );
          setContacts(valid);
        }
      })
      .catch(err => {
        console.warn("Error fetching contacts:", err?.message);
      })
      .finally(() => {
        if (!isCancelled) setLoadingContacts(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [createNewChat, searchText]);

  const conversationItems = (users || [])
    .map(chat => {
      const partner = extractPartner(chat, authUser);
      const lastMsg =
        chat?.lastMessageSnapshot?.text ||
        chat?.messages?.[chat?.messages?.length - 1]?.message ||
        "";
      return {
        chat,
        partner,
        lastMessage: lastMsg,
        conversationId: chat?._id,
        hasUnread: Boolean(chat?.hasUnread)
      };
    })
    .filter(item => {
      if (!item.partner || !item.partner._id) return false;
      if (!searchText) return true;
      const term = searchText.toUpperCase();
      const fn = (item.partner.firstName || "").toUpperCase();
      const ln = (item.partner.lastName || "").toUpperCase();
      return fn.includes(term) || ln.includes(term);
    });

  // Rows are buttons for keyboard users; Enter/Space opens the conversation.
  const rowProps = onOpen => ({
    role: "button",
    tabIndex: 0,
    onClick: onOpen,
    onKeyDown: e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen();
      }
    }
  });

  const handleUserClick = (partner, chat) => {
    if (!partner || !partner._id || partner._id === "undefined" || partner._id === "null") {
      return;
    }
    onSelectUser(partner, partner.role, chat);
  };

  return (
    <div className="chat-users">
      <div className="chat-users__header">
        <div className="chat-users__title-row">
          <h3 className="chat-users__title">{t("Chats")}</h3>
          <Tooltip title={t("New chat")}>
            <Button
              type="text"
              icon={<BiMessageDetail />}
              className="chat-users__new-btn"
              aria-label={t("New chat")}
              aria-pressed={createNewChat}
              onClick={() => setCreateNewChat(!createNewChat)}
            />
          </Tooltip>
        </div>
        <Search
          allowClear
          type="search"
          enterKeyHint="search"
          placeholder={t("Search or start a new chat")}
          onChange={handleSearch}
        />
      </div>
      <div className="chat-users__list">
        {!createNewChat ? (
          <List
            itemLayout="horizontal"
            dataSource={conversationItems}
            locale={{ emptyText: t("No conversations yet") }}
            renderItem={({ partner, lastMessage, chat, hasUnread }) => {
              const isSelected =
                selectedUser &&
                (String(selectedUser) === String(partner._id) ||
                  (chat?._id && String(selectedUser) === String(chat._id)));
              const displayName = `${partner.firstName || ""} ${partner.lastName || ""}`.trim() || t("User");
              const avatarSrc = partner.picture?.url || (typeof partner.picture === "string" ? partner.picture : null);

              return (
                <List.Item
                  className={`chat-users__item${isSelected ? " is-active" : ""}`}
                  {...rowProps(() => handleUserClick(partner, chat))}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size={44}
                        src={avatarSrc}
                        icon={!avatarSrc && <UserOutlined />}
                        className="chat-users__avatar"
                      />
                    }
                    title={
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>{displayName}</span>
                        {hasUnread && <span className="chat-users__unread-dot" />}
                      </div>
                    }
                    description={lastMessage}
                  />
                </List.Item>
              );
            }}
          />
        ) : loadingContacts ? (
          <div style={{ textAlign: "center", padding: "24px" }}>
            <Spin />
          </div>
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={contacts}
            locale={{ emptyText: t("No users found") }}
            renderItem={user => {
              const isSelected = selectedUser && String(selectedUser) === String(user._id);
              const displayName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.username || user.email || t("User");
              const avatarSrc = user.picture?.url || (typeof user.picture === "string" ? user.picture : null);
              const subtitle = user.email || (user.userType ? t(user.userType) : "");

              return (
                <List.Item
                  className={`chat-users__item${isSelected ? " is-active" : ""}`}
                  {...rowProps(() => handleUserClick(user))}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        size={44}
                        src={avatarSrc}
                        icon={!avatarSrc && <UserOutlined />}
                        className="chat-users__avatar"
                      />
                    }
                    title={displayName}
                    description={subtitle}
                  />
                </List.Item>
              );
            }}
          />
        )}
      </div>
    </div>
  );
};

export default UserList;
